import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Heart, Building2, ArrowLeft, ArrowRight, MapPin, User, FileText,
  Check, Loader2, Upload, AlertCircle, Users, HandHeart, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { TIPOS_ENTIDADE, CARGOS_CONTACTO } from '../constants/cargos';

// Respostas Sociais com estrutura de acordos
const RESPOSTAS_SOCIAIS_CONFIG = [
  { 
    id: 'creche', 
    label: 'Creche',
    acordos: ['Acordo SS', 'Acordo CMx', 'Acordo IEFP', 'Sem Acordo']
  },
  { 
    id: 'jardim_infantil', 
    label: 'Jardim de Infância',
    acordos: ['Acordo SS', 'Acordo CMx', 'Acordo IEFP', 'Sem Acordo']
  },
  { 
    id: 'catl', 
    label: 'CATL',
    acordos: ['Acordo SS', 'Acordo CMx', 'Acordo IEFP', 'Sem Acordo']
  },
  { 
    id: 'sad', 
    label: 'SAD - Serviço de Apoio Domiciliário',
    acordos: ['Acordo SS', 'Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'centro_dia', 
    label: 'Centro de Dia',
    acordos: ['Acordo SS', 'Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'centro_convivio', 
    label: 'Centro de Convívio',
    acordos: ['Acordo SS', 'Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'erpi', 
    label: 'ERPI - Estrutura Residencial para Pessoas Idosas',
    acordos: ['Acordo SS', 'Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'cao', 
    label: 'CAO - Centro de Atividades Ocupacionais',
    acordos: ['Acordo SS', 'Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'lar_residencial', 
    label: 'Lar Residencial',
    acordos: ['Acordo SS', 'Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'acompanhamento_social', 
    label: 'Acompanhamento Social',
    acordos: ['Protocolo CMx', 'Sem Acordo']
  },
  { 
    id: 'distribuicao_bens', 
    label: 'Distribuição de Bens',
    tipoEspecial: 'distribuicao',
    subCampos: [
      { id: 'alimentares_familias', label: 'Bens Alimentares - Nº Famílias' },
      { id: 'alimentares_pessoas', label: 'Bens Alimentares - Nº Pessoas' },
      { id: 'nao_alimentares_familias', label: 'Bens Não Alimentares - Nº Famílias' },
      { id: 'nao_alimentares_pessoas', label: 'Bens Não Alimentares - Nº Pessoas' }
    ]
  }
];

// Áreas de apoio ENTRAJUDA (SEM "Saúde Oral" conforme especificação)
const AREAS_ENTRAJUDA = [
  { id: 'apoio_gestao', label: 'APOIO À GESTÃO E ORGANIZAÇÃO', desc: 'Consultadoria de apoio à gestão e organização da Instituição' },
  { id: 'banco_bens', label: 'BANCO DE BENS DOADOS', desc: 'Distribuição de produtos não alimentares e equipamento informático' },
  { id: 'formacao', label: 'FORMAÇÃO', desc: 'Acções de formação' },
  { id: 'projectos_solidarios', label: 'PROJECTOS SOLIDÁRIOS', desc: 'Acções pontuais realizadas no âmbito do Voluntariado-Empresa' },
  { id: 'bolsa_voluntariado', label: 'BOLSA DO VOLUNTARIADO', desc: 'Voluntários qualificados online (www.bolsadovoluntariado.pt)' }
];

// Tipos de documentos para upload
const DOCUMENTOS_CANDIDATURA = [
  { id: 'estatutos', label: 'Estatutos da Instituição (assinados)', obrigatorio: true },
  { id: 'relatorio_contas', label: 'Relatório e Contas (último ano - aprovado e assinado)', obrigatorio: false },
  { id: 'plano_atividades', label: 'Plano de Atividades', obrigatorio: false }
];

export default function PortalRegistarPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [distritos, setDistritos] = useState([]);
  const [concelhos, setConcelhos] = useState([]);
  const [freguesias, setFreguesias] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // Form data - organized by section
  const [formData, setFormData] = useState({
    // ========================================
    // SECÇÃO 1 - Identificação da Instituição
    // ========================================
    nome: '',
    morada: '',
    numero_porta: '',
    codigo_postal: '',
    localidade: '',
    distrito_id: '',
    concelho_id: '',
    freguesia: '',
    nif: '',
    telefone: '',
    telemovel: '',
    email: '',
    // Presidente da Direção (OBRIGATÓRIO)
    presidente_nome: '',
    presidente_email: '',
    presidente_telefone: '',
    // Pessoa de contacto (OBRIGATÓRIO)
    contacto_nome: '',
    contacto_cargo: '',
    contacto_email: '',
    contacto_telefone: '',
    ano_inicio_atividade: '',
    website: '',
    // Redes Sociais
    redes_sociais: {
      facebook: '',
      instagram: '',
      linkedin: '',
      tiktok: '',
      youtube: '',
      twitter: ''
    },
    
    // ========================================
    // SECÇÃO 2 - Caracterização da Instituição
    // ========================================
    forma_juridica: '',
    forma_juridica_outro: '',
    missao: '', // OBRIGATÓRIO
    area_geografica: '', // OBRIGATÓRIO
    num_colaboradores: '', // OBRIGATÓRIO
    num_voluntarios: '', // OBRIGATÓRIO
    
    // ========================================
    // SECÇÃO 3 - Respostas Sociais (NOVA ESTRUTURA)
    // ========================================
    respostas_sociais: {}, // { [id]: { ativo: bool, acordo: string, num_utentes: number } }
    distribuicao_bens: {
      alimentares_familias: '',
      alimentares_pessoas: '',
      nao_alimentares_familias: '',
      nao_alimentares_pessoas: ''
    },
    outras_respostas_sociais: '',
    total_utentes_apoiados: 0, // Auto-calculado
    
    // ========================================
    // SECÇÃO 4 - Informações Adicionais e Documentos
    // ========================================
    areas_interesse: [], // NÃO obrigatório
    comentarios_adicionais: '',
    submetido_por_nome: '', // OBRIGATÓRIO
    submetido_por_cargo: '', // OBRIGATÓRIO
    
    // Documentos
    documentos: {
      estatutos: null, // OBRIGATÓRIO
      relatorio_contas: null,
      plano_atividades: null
    },
    
    aceita_termos: false
  });

  useEffect(() => {
    loadDistritos();
  }, []);

  useEffect(() => {
    if (formData.distrito_id) {
      loadConcelhos(formData.distrito_id);
    }
  }, [formData.distrito_id]);

  // Integração CTT - Auto-fill com Código Postal
  useEffect(() => {
    const fetchCTTData = async () => {
      const cp = formData.codigo_postal;
      if (cp && cp.length >= 7) { // Aceita XXXX-XXX ou XXXXXXX
        const cpClean = cp.replace('-', '').replace(' ', '');
        if (cpClean.length >= 4) {
          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/codigos-postais/${cpClean}`);
            if (response.ok) {
              const data = await response.json();
              if (data.distrito_id && !data.error) {
                // Carregar concelhos do distrito primeiro
                if (data.distrito_id) {
                  await loadConcelhos(data.distrito_id);
                }
                
                // Atualizar todos os campos numa única atualização de estado
                setFormData(prev => ({
                  ...prev,
                  distrito_id: data.distrito_id,
                  concelho_id: data.concelho_id || prev.concelho_id,
                  freguesia: data.freguesia || prev.freguesia,
                  localidade: data.localidade || prev.localidade,
                  // Se há rua na resposta e morada está vazia, preencher
                  morada: data.rua && !prev.morada ? data.rua : prev.morada
                }));
                
                // Mostrar feedback ao utilizador
                const feedbackParts = [];
                if (data.rua) feedbackParts.push(data.rua);
                if (data.freguesia) feedbackParts.push(data.freguesia);
                if (data.concelho) feedbackParts.push(data.concelho);
                toast.success(`Localização: ${feedbackParts.join(', ') || data.distrito}`);
              }
            }
          } catch (error) {
            console.error('Error fetching CTT data:', error);
          }
        }
      }
    };
    fetchCTTData();
  }, [formData.codigo_postal]);

  // Calcular total de utentes automaticamente
  useEffect(() => {
    let total = 0;
    Object.values(formData.respostas_sociais).forEach(rs => {
      if (rs.ativo && rs.num_utentes) {
        total += parseInt(rs.num_utentes) || 0;
      }
    });
    // Adicionar distribuição de bens (pessoas)
    total += parseInt(formData.distribuicao_bens.alimentares_pessoas) || 0;
    total += parseInt(formData.distribuicao_bens.nao_alimentares_pessoas) || 0;
    
    setFormData(prev => ({ ...prev, total_utentes_apoiados: total }));
  }, [formData.respostas_sociais, formData.distribuicao_bens]);

  const loadDistritos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`);
      const data = await response.json();
      setDistritos(data);
    } catch (error) {
      console.error('Error loading distritos:', error);
    }
  };

  const loadConcelhos = async (distritoId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos/${distritoId}/concelhos`);
      if (response.ok) {
        const data = await response.json();
        setConcelhos(data);
      }
    } catch (error) {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concelhos?distrito_id=${distritoId}`);
      if (response.ok) {
        const data = await response.json();
        setConcelhos(data);
      }
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const updateRespostaSocial = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      respostas_sociais: {
        ...prev.respostas_sociais,
        [id]: {
          ...prev.respostas_sociais[id],
          [field]: value
        }
      }
    }));
  };

  const updateDistribuicaoBens = (field, value) => {
    setFormData(prev => ({
      ...prev,
      distribuicao_bens: {
        ...prev.distribuicao_bens,
        [field]: value
      }
    }));
  };

  const updateRedeSocial = (rede, value) => {
    setFormData(prev => ({
      ...prev,
      redes_sociais: {
        ...prev.redes_sociais,
        [rede]: value
      }
    }));
  };

  const toggleAreaInteresse = (areaId) => {
    setFormData(prev => ({
      ...prev,
      areas_interesse: prev.areas_interesse.includes(areaId)
        ? prev.areas_interesse.filter(id => id !== areaId)
        : [...prev.areas_interesse, areaId]
    }));
  };

  // Upload de documento
  const handleDocumentUpload = async (docId, file) => {
    if (!file) return;
    
    setUploadingDoc(docId);
    try {
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          documentos: {
            ...prev.documentos,
            [docId]: {
              nome: file.name,
              tipo: file.type,
              tamanho: file.size,
              base64: reader.result
            }
          }
        }));
        setUploadingDoc(null);
        toast.success(`Documento "${file.name}" carregado com sucesso`);
      };
      reader.onerror = () => {
        toast.error('Erro ao carregar documento');
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erro ao processar documento');
      setUploadingDoc(null);
    }
  };

  const removeDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      documentos: {
        ...prev.documentos,
        [docId]: null
      }
    }));
  };

  const validateStep = (stepNum) => {
    const errors = {};
    let firstErrorField = null;
    
    const fieldLabels = {
      nome: 'Nome da Instituição',
      nif: 'NIF',
      distrito_id: 'Distrito',
      concelho_id: 'Concelho',
      morada: 'Morada',
      codigo_postal: 'Código Postal',
      email: 'Email',
      presidente_nome: 'Nome do Presidente da Direção',
      presidente_email: 'Email do Presidente',
      presidente_telefone: 'Telefone do Presidente',
      contacto_nome: 'Nome da Pessoa de Contacto',
      contacto_email: 'Email da Pessoa de Contacto',
      contacto_telefone: 'Telefone da Pessoa de Contacto',
      forma_juridica: 'Forma Jurídica',
      forma_juridica_outro: 'Especificação da Forma Jurídica',
      missao: 'Missão',
      area_geografica: 'Área Geográfica de Atuação',
      num_colaboradores: 'Nº de Colaboradores',
      num_voluntarios: 'Nº de Voluntários',
      submetido_por_nome: 'Nome de quem submete',
      submetido_por_cargo: 'Cargo de quem submete',
      aceita_termos: 'Termos de Uso',
      estatutos: 'Estatutos da Instituição'
    };
    
    switch (stepNum) {
      case 1:
        // Campos obrigatórios Step 1
        // Verificar se é Internacional (não obriga concelho/freguesia)
        const isInternacional = distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional';
        
        const requiredStep1 = [
          'nome', 'nif', 'distrito_id', 'morada', 'codigo_postal', 'email',
          'presidente_nome', 'presidente_email', 'presidente_telefone',
          'contacto_nome', 'contacto_email', 'contacto_telefone'
        ];
        
        // Concelho só é obrigatório se NÃO for Internacional
        if (!isInternacional) {
          requiredStep1.push('concelho_id');
        }
        
        requiredStep1.forEach(field => {
          if (!formData[field]) {
            errors[field] = `${fieldLabels[field]} é obrigatório`;
            if (!firstErrorField) firstErrorField = field;
          }
        });
        
        if (formData.nif && !/^\d{9}$/.test(formData.nif)) {
          errors.nif = 'NIF inválido (deve ter 9 dígitos)';
          if (!firstErrorField) firstErrorField = 'nif';
        }
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          errors.email = 'Email inválido';
          if (!firstErrorField) firstErrorField = 'email';
        }
        break;
        
      case 2:
        // Campos obrigatórios Step 2
        if (!formData.forma_juridica) {
          errors.forma_juridica = 'Forma Jurídica é obrigatória';
          firstErrorField = 'forma_juridica';
        }
        if (formData.forma_juridica === 'Outra' && !formData.forma_juridica_outro) {
          errors.forma_juridica_outro = 'Especifique a forma jurídica';
          if (!firstErrorField) firstErrorField = 'forma_juridica_outro';
        }
        if (!formData.missao) {
          errors.missao = 'Missão é obrigatória';
          if (!firstErrorField) firstErrorField = 'missao';
        }
        if (!formData.area_geografica) {
          errors.area_geografica = 'Área Geográfica de Atuação é obrigatória';
          if (!firstErrorField) firstErrorField = 'area_geografica';
        }
        if (!formData.num_colaboradores) {
          errors.num_colaboradores = 'Nº de Colaboradores é obrigatório';
          if (!firstErrorField) firstErrorField = 'num_colaboradores';
        }
        if (!formData.num_voluntarios) {
          errors.num_voluntarios = 'Nº de Voluntários é obrigatório';
          if (!firstErrorField) firstErrorField = 'num_voluntarios';
        }
        break;
        
      case 3:
        // Step 3 - Respostas Sociais (não obrigatório)
        break;
        
      case 4:
        if (!formData.submetido_por_nome) {
          errors.submetido_por_nome = 'Nome é obrigatório';
          firstErrorField = 'submetido_por_nome';
        }
        if (!formData.submetido_por_cargo) {
          errors.submetido_por_cargo = 'Cargo é obrigatório';
          if (!firstErrorField) firstErrorField = 'submetido_por_cargo';
        }
        // Documento obrigatório: Estatutos
        if (!formData.documentos.estatutos) {
          errors.estatutos = 'Estatutos da Instituição são obrigatórios';
          if (!firstErrorField) firstErrorField = 'estatutos';
        }
        if (!formData.aceita_termos) {
          errors.aceita_termos = 'Deve aceitar os Termos de Uso';
          if (!firstErrorField) firstErrorField = 'aceita_termos';
        }
        break;
        
      default:
        break;
    }
    
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Build error message with specific fields
      const errorFields = Object.keys(errors).map(k => fieldLabels[k] || k);
      const errorMessage = errorFields.length === 1 
        ? `Preencha o campo: ${errorFields[0]}`
        : `Preencha os campos em falta: ${errorFields.slice(0, 3).join(', ')}${errorFields.length > 3 ? ` (+${errorFields.length - 3})` : ''}`;
      
      toast.error(errorMessage);
      
      // Scroll to first error field
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus?.();
          }
        }, 100);
      }
      
      return false;
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      const submitData = {
        // Identificação
        nome: formData.nome,
        morada: formData.morada,
        numero_porta: formData.numero_porta,
        codigo_postal: formData.codigo_postal,
        localidade: formData.localidade,
        distrito_id: formData.distrito_id,
        concelho_id: formData.concelho_id,
        freguesia: formData.freguesia,
        nif: formData.nif,
        telefone: formData.telefone,
        telemovel: formData.telemovel,
        email: formData.email,
        website: formData.website,
        ano_inicio_atividade: formData.ano_inicio_atividade ? parseInt(formData.ano_inicio_atividade) : null,
        
        // Redes Sociais
        redes_sociais: formData.redes_sociais,
        
        // Presidente da Direção
        presidente: {
          nome: formData.presidente_nome,
          email: formData.presidente_email,
          telefone: formData.presidente_telefone
        },
        
        // Pessoa de Contacto
        contacto: {
          nome: formData.contacto_nome,
          cargo: formData.contacto_cargo,
          email: formData.contacto_email,
          telefone: formData.contacto_telefone
        },
        
        // Caracterização
        tipo_entidade: formData.forma_juridica === 'Outra' ? formData.forma_juridica_outro : formData.forma_juridica,
        forma_juridica: formData.forma_juridica,
        forma_juridica_outro: formData.forma_juridica_outro,
        missao: formData.missao,
        area_geografica: formData.area_geografica,
        num_colaboradores: formData.num_colaboradores ? parseInt(formData.num_colaboradores) : 0,
        num_voluntarios: formData.num_voluntarios ? parseInt(formData.num_voluntarios) : 0,
        
        // Respostas Sociais (nova estrutura)
        respostas_sociais: formData.respostas_sociais,
        distribuicao_bens: {
          alimentares_familias: parseInt(formData.distribuicao_bens.alimentares_familias) || 0,
          alimentares_pessoas: parseInt(formData.distribuicao_bens.alimentares_pessoas) || 0,
          nao_alimentares_familias: parseInt(formData.distribuicao_bens.nao_alimentares_familias) || 0,
          nao_alimentares_pessoas: parseInt(formData.distribuicao_bens.nao_alimentares_pessoas) || 0
        },
        outras_respostas_sociais: formData.outras_respostas_sociais,
        total_utentes_apoiados: formData.total_utentes_apoiados,
        
        // Informações Adicionais
        areas_interesse: formData.areas_interesse,
        comentarios_adicionais: formData.comentarios_adicionais,
        submetido_por: {
          nome: formData.submetido_por_nome,
          cargo: formData.submetido_por_cargo
        },
        
        // Documentos
        documentos: formData.documentos,
        
        aceita_termos: formData.aceita_termos
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/registar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Candidatura submetida com sucesso!');
        navigate('/portal/candidatura-submetida', { state: { candidatura_id: data.candidatura_id } });
      } else {
        toast.error(data.detail || 'Erro ao submeter candidatura');
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // SECÇÃO 1 - Identificação da Instituição
  const renderStep1 = () => (
    <div className="space-y-6" data-testid="step-1">
      {/* 1. Nome */}
      <div className="space-y-2">
        <Label className="font-semibold">1. Nome *</Label>
        <Input
          id="field-nome"
          value={formData.nome}
          onChange={(e) => { updateField('nome', e.target.value); setFieldErrors(prev => ({...prev, nome: null})); }}
          placeholder="Nome completo da instituição"
          data-testid="input-nome"
          className={fieldErrors.nome ? 'border-red-500 focus:ring-red-500' : ''}
        />
        {fieldErrors.nome && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.nome}</p>}
      </div>

      {/* 2. Morada */}
      <div className="space-y-3">
        <Label className="font-semibold">2. Morada</Label>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-3 space-y-2">
            <Label className="text-sm text-slate-600">Morada *</Label>
            <Input
              id="field-morada"
              value={formData.morada}
              onChange={(e) => { updateField('morada', e.target.value); setFieldErrors(prev => ({...prev, morada: null})); }}
              placeholder="Rua..."
              data-testid="input-morada"
              className={fieldErrors.morada ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.morada && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.morada}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Nº Porta</Label>
            <Input
              id="field-numero_porta"
              value={formData.numero_porta}
              onChange={(e) => updateField('numero_porta', e.target.value)}
              placeholder="Nº"
              data-testid="input-numero-porta"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">CP *</Label>
          <Input
            id="field-codigo_postal"
            value={formData.codigo_postal}
            onChange={(e) => {
              let v = e.target.value.replace(/[^\d-]/g, '');
              if (v.length === 4 && !v.includes('-')) v += '-';
              updateField('codigo_postal', v.slice(0, 8));
              setFieldErrors(prev => ({...prev, codigo_postal: null}));
            }}
            placeholder="0000-000"
            maxLength={8}
            data-testid="input-cp"
            className={fieldErrors.codigo_postal ? 'border-red-500 focus:ring-red-500' : ''}
          />
          {fieldErrors.codigo_postal && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.codigo_postal}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Localidade</Label>
          <Input
            value={formData.localidade}
            onChange={(e) => updateField('localidade', e.target.value)}
            placeholder="Localidade"
          />
        </div>
      </div>

      {/* 3. Localização */}
      <div className="space-y-3">
        <Label className="font-semibold">3. Localização</Label>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Preenchimento automático:</strong> Ao introduzir o Código Postal válido, os campos Distrito, Concelho, Freguesia e Morada são preenchidos automaticamente com base nos dados CTT.
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Distrito *</Label>
            <Select value={formData.distrito_id} onValueChange={(v) => { updateField('distrito_id', v); setFieldErrors(prev => ({...prev, distrito_id: null})); }}>
              <SelectTrigger id="field-distrito_id" data-testid="select-distrito" className={fieldErrors.distrito_id ? 'border-red-500' : ''}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {distritos.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.distrito_id && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.distrito_id}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">
              Concelho {distritos.find(d => d.id === formData.distrito_id)?.nome !== 'Internacional' ? '*' : '(opcional)'}
            </Label>
            <Select 
              value={formData.concelho_id} 
              onValueChange={(v) => { updateField('concelho_id', v); setFieldErrors(prev => ({...prev, concelho_id: null})); }}
              disabled={!formData.distrito_id || distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional'}
            >
              <SelectTrigger id="field-concelho_id" data-testid="select-concelho" className={fieldErrors.concelho_id ? 'border-red-500' : ''}>
                <SelectValue placeholder={distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? 'N/A (Internacional)' : 'Selecione'} />
              </SelectTrigger>
              <SelectContent>
                {concelhos.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.concelho_id && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.concelho_id}</p>}
          </div>
        </div>
        {distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            Para instituições internacionais, o preenchimento de Concelho e Freguesia é opcional.
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Freguesia {distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? '(opcional)' : ''}</Label>
          <Input
            value={formData.freguesia}
            onChange={(e) => updateField('freguesia', e.target.value)}
            placeholder="Freguesia (preenchida automaticamente pelo CP)"
            data-testid="input-freguesia"
            disabled={distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional'}
          />
        </div>
      </div>

      {/* 4. Identificação Fiscal */}
      <div className="space-y-2">
        <Label className="font-semibold">4. Identificação Fiscal</Label>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">NIF *</Label>
          <Input
            id="field-nif"
            value={formData.nif}
            onChange={(e) => { updateField('nif', e.target.value.replace(/\D/g, '').slice(0, 9)); setFieldErrors(prev => ({...prev, nif: null})); }}
            placeholder="000000000"
            maxLength={9}
            data-testid="input-nif"
            className={fieldErrors.nif ? 'border-red-500 focus:ring-red-500' : ''}
          />
          {fieldErrors.nif && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.nif}</p>}
        </div>
      </div>

      {/* 5. Contactos da instituição */}
      <div className="space-y-3">
        <Label className="font-semibold">5. Contactos da instituição</Label>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Telefone</Label>
          <Input
            value={formData.telefone}
            onChange={(e) => updateField('telefone', e.target.value)}
            placeholder="212345678"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Telemóvel</Label>
          <Input
            value={formData.telemovel}
            onChange={(e) => updateField('telemovel', e.target.value)}
            placeholder="912345678"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Email *</Label>
          <Input
            id="field-email"
            type="email"
            value={formData.email}
            onChange={(e) => { updateField('email', e.target.value); setFieldErrors(prev => ({...prev, email: null})); }}
            placeholder="geral@instituicao.pt"
            data-testid="input-email"
            className={fieldErrors.email ? 'border-red-500 focus:ring-red-500' : ''}
          />
          {fieldErrors.email && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.email}</p>}
        </div>
      </div>

      {/* 6. Presidente da Direção */}
      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
        <Label className="font-semibold">6. Presidente da Direção *</Label>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Nome *</Label>
          <Input
            id="field-presidente_nome"
            value={formData.presidente_nome}
            onChange={(e) => updateField('presidente_nome', e.target.value)}
            placeholder="Nome do presidente"
            data-testid="input-presidente-nome"
            className={fieldErrors.presidente_nome ? 'border-red-500 focus:ring-red-500' : ''}
          />
          {fieldErrors.presidente_nome && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.presidente_nome}</p>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Email *</Label>
            <Input
              id="field-presidente_email"
              type="email"
              value={formData.presidente_email}
              onChange={(e) => updateField('presidente_email', e.target.value)}
              placeholder="presidente@instituicao.pt"
              data-testid="input-presidente-email"
              className={fieldErrors.presidente_email ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.presidente_email && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.presidente_email}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Telefone *</Label>
            <Input
              id="field-presidente_telefone"
              value={formData.presidente_telefone}
              onChange={(e) => updateField('presidente_telefone', e.target.value)}
              placeholder="912345678"
              data-testid="input-presidente-telefone"
              className={fieldErrors.presidente_telefone ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.presidente_telefone && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.presidente_telefone}</p>}
          </div>
        </div>
      </div>

      {/* 7. Pessoa de contacto */}
      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
        <Label className="font-semibold">7. Pessoa de Contacto *</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Nome *</Label>
            <Input
              id="field-contacto_nome"
              value={formData.contacto_nome}
              onChange={(e) => updateField('contacto_nome', e.target.value)}
              placeholder="Nome"
              data-testid="input-contacto-nome"
              className={fieldErrors.contacto_nome ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.contacto_nome && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.contacto_nome}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Cargo</Label>
            <Select 
              value={formData.contacto_cargo} 
              onValueChange={(v) => updateField('contacto_cargo', v)}
            >
              <SelectTrigger data-testid="select-contacto-cargo">
                <SelectValue placeholder="Selecione o cargo" />
              </SelectTrigger>
              <SelectContent>
                {CARGOS_CONTACTO.map(cargo => (
                  <SelectItem key={cargo} value={cargo}>{cargo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Email *</Label>
            <Input
              id="field-contacto_email"
              type="email"
              value={formData.contacto_email}
              onChange={(e) => updateField('contacto_email', e.target.value)}
              placeholder="contacto@instituicao.pt"
              data-testid="input-contacto-email"
              className={fieldErrors.contacto_email ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.contacto_email && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.contacto_email}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Telefone *</Label>
            <Input
              id="field-contacto_telefone"
              value={formData.contacto_telefone}
              onChange={(e) => updateField('contacto_telefone', e.target.value)}
              placeholder="912345678"
              data-testid="input-contacto-telefone"
              className={fieldErrors.contacto_telefone ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.contacto_telefone && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.contacto_telefone}</p>}
          </div>
        </div>
      </div>

      {/* 8. Início de Actividade */}
      <div className="space-y-2">
        <Label className="font-semibold">8. Início de Actividade</Label>
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Ano</Label>
          <Input
            type="number"
            value={formData.ano_inicio_atividade}
            onChange={(e) => updateField('ano_inicio_atividade', e.target.value)}
            placeholder="Ex: 1990"
            min="1800"
            max={new Date().getFullYear()}
            className="w-32"
          />
        </div>
      </div>

      {/* 9. Website */}
      <div className="space-y-2">
        <Label className="font-semibold">9. Website</Label>
        <Input
          type="url"
          value={formData.website}
          onChange={(e) => updateField('website', e.target.value)}
          placeholder="https://www.exemplo.pt"
        />
      </div>

      {/* 10. Redes Sociais */}
      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
        <Label className="font-semibold">10. Redes Sociais</Label>
        <p className="text-xs text-slate-500">Preencha pelo menos uma rede social (opcional)</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </Label>
            <Input
              value={formData.redes_sociais.facebook}
              onChange={(e) => updateRedeSocial('facebook', e.target.value)}
              placeholder="https://facebook.com/..."
              data-testid="input-facebook"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </Label>
            <Input
              value={formData.redes_sociais.instagram}
              onChange={(e) => updateRedeSocial('instagram', e.target.value)}
              placeholder="https://instagram.com/..."
              data-testid="input-instagram"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </Label>
            <Input
              value={formData.redes_sociais.linkedin}
              onChange={(e) => updateRedeSocial('linkedin', e.target.value)}
              placeholder="https://linkedin.com/company/..."
              data-testid="input-linkedin"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              TikTok
            </Label>
            <Input
              value={formData.redes_sociais.tiktok}
              onChange={(e) => updateRedeSocial('tiktok', e.target.value)}
              placeholder="https://tiktok.com/@..."
              data-testid="input-tiktok"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // SECÇÃO 2 - Caracterização da Instituição
  const renderStep2 = () => (
    <div className="space-y-6" data-testid="step-2">
      {/* 10. Forma Jurídica */}
      <div className="space-y-3">
        <Label className="font-semibold">10. Forma Jurídica *</Label>
        <RadioGroup 
          value={formData.forma_juridica} 
          onValueChange={(v) => updateField('forma_juridica', v)}
          className="grid grid-cols-2 gap-2"
        >
          {TIPOS_ENTIDADE.map(tipo => (
            <div key={tipo} className="flex items-center space-x-2">
              <RadioGroupItem value={tipo} id={`forma-${tipo}`} />
              <Label htmlFor={`forma-${tipo}`} className="cursor-pointer text-sm">
                {tipo}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {fieldErrors.forma_juridica && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.forma_juridica}</p>}
        {formData.forma_juridica === 'Outra' && (
          <Input
            id="field-forma_juridica_outro"
            value={formData.forma_juridica_outro}
            onChange={(e) => updateField('forma_juridica_outro', e.target.value)}
            placeholder="Especifique..."
            className={`mt-2 ${fieldErrors.forma_juridica_outro ? 'border-red-500' : ''}`}
          />
        )}
      </div>

      {/* 11. Missão */}
      <div className="space-y-2">
        <Label className="font-semibold">11. Missão *</Label>
        <Textarea
          id="field-missao"
          value={formData.missao}
          onChange={(e) => updateField('missao', e.target.value)}
          placeholder="Descreva a missão da instituição..."
          rows={3}
          className={fieldErrors.missao ? 'border-red-500 focus:ring-red-500' : ''}
        />
        {fieldErrors.missao && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.missao}</p>}
      </div>

      {/* 12. Área geográfica de actuação (renumerado - removida pergunta "Caracterização da População") */}
      <div className="space-y-2">
        <Label className="font-semibold">12. Área Geográfica de Atuação *</Label>
        <Input
          id="field-area_geografica"
          value={formData.area_geografica}
          onChange={(e) => updateField('area_geografica', e.target.value)}
          placeholder="Ex: Concelho de Lisboa"
          className={fieldErrors.area_geografica ? 'border-red-500 focus:ring-red-500' : ''}
        />
        {fieldErrors.area_geografica && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.area_geografica}</p>}
      </div>

      {/* 13. Nº de Colaboradores e Voluntários (renumerado) */}
      <div className="space-y-3">
        <Label className="font-semibold">13. Nº de Colaboradores e Voluntários *</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Colaboradores *</Label>
            <Input
              id="field-num_colaboradores"
              type="number"
              value={formData.num_colaboradores}
              onChange={(e) => updateField('num_colaboradores', e.target.value)}
              min="0"
              className={`w-32 ${fieldErrors.num_colaboradores ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {fieldErrors.num_colaboradores && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.num_colaboradores}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Voluntários *</Label>
            <Input
              id="field-num_voluntarios"
              type="number"
              value={formData.num_voluntarios}
              onChange={(e) => updateField('num_voluntarios', e.target.value)}
              min="0"
              className={`w-32 ${fieldErrors.num_voluntarios ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {fieldErrors.num_voluntarios && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.num_voluntarios}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  // SECÇÃO 3 - Respostas Sociais da Instituição (NOVA ESTRUTURA)
  const renderStep3 = () => (
    <div className="space-y-6" data-testid="step-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-800">
          <strong>Instruções:</strong> Para cada resposta social, indique se a instituição oferece esse serviço. 
          Se sim, selecione o tipo de acordo e indique o número de utentes.
        </p>
      </div>

      {/* Respostas Sociais com estrutura Sim/Não → Acordos → Utentes */}
      <div className="space-y-4">
        <Label className="font-semibold text-lg">15. Respostas Sociais da Instituição</Label>
        
        {RESPOSTAS_SOCIAIS_CONFIG.filter(rs => rs.id !== 'distribuicao_bens').map(resposta => {
          const rsData = formData.respostas_sociais[resposta.id] || { ativo: false, acordo: '', num_utentes: '' };
          
          return (
            <div key={resposta.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">{resposta.label}</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`rs-${resposta.id}`}
                      checked={rsData.ativo === true}
                      onChange={() => updateRespostaSocial(resposta.id, 'ativo', true)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Sim</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`rs-${resposta.id}`}
                      checked={rsData.ativo === false}
                      onChange={() => updateRespostaSocial(resposta.id, 'ativo', false)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Não</span>
                  </label>
                </div>
              </div>
              
              {rsData.ativo && (
                <div className="ml-4 pl-4 border-l-2 border-blue-200 space-y-3 animate-in slide-in-from-top-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Tipo de Acordo</Label>
                      <Select 
                        value={rsData.acordo || ''} 
                        onValueChange={(v) => updateRespostaSocial(resposta.id, 'acordo', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o acordo" />
                        </SelectTrigger>
                        <SelectContent>
                          {resposta.acordos.map(acordo => (
                            <SelectItem key={acordo} value={acordo}>{acordo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Nº de Utentes</Label>
                      <Input
                        type="number"
                        value={rsData.num_utentes || ''}
                        onChange={(e) => updateRespostaSocial(resposta.id, 'num_utentes', e.target.value)}
                        min="0"
                        placeholder="0"
                        className="w-32"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Distribuição de Bens */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="font-medium">Distribuição de Bens</Label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="rs-distribuicao"
                checked={formData.respostas_sociais.distribuicao_bens?.ativo === true}
                onChange={() => updateRespostaSocial('distribuicao_bens', 'ativo', true)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Sim</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="rs-distribuicao"
                checked={formData.respostas_sociais.distribuicao_bens?.ativo !== true}
                onChange={() => updateRespostaSocial('distribuicao_bens', 'ativo', false)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Não</span>
            </label>
          </div>
        </div>
        
        {formData.respostas_sociais.distribuicao_bens?.ativo && (
          <div className="ml-4 pl-4 border-l-2 border-blue-200 space-y-3 animate-in slide-in-from-top-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Bens Alimentares - Nº Famílias</Label>
                <Input
                  type="number"
                  value={formData.distribuicao_bens.alimentares_familias}
                  onChange={(e) => updateDistribuicaoBens('alimentares_familias', e.target.value)}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Bens Alimentares - Nº Pessoas</Label>
                <Input
                  type="number"
                  value={formData.distribuicao_bens.alimentares_pessoas}
                  onChange={(e) => updateDistribuicaoBens('alimentares_pessoas', e.target.value)}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Bens Não Alimentares - Nº Famílias</Label>
                <Input
                  type="number"
                  value={formData.distribuicao_bens.nao_alimentares_familias}
                  onChange={(e) => updateDistribuicaoBens('nao_alimentares_familias', e.target.value)}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Bens Não Alimentares - Nº Pessoas</Label>
                <Input
                  type="number"
                  value={formData.distribuicao_bens.nao_alimentares_pessoas}
                  onChange={(e) => updateDistribuicaoBens('nao_alimentares_pessoas', e.target.value)}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Outras Respostas Sociais */}
      <div className="space-y-2">
        <Label className="font-semibold">16. Outras Respostas Sociais</Label>
        <p className="text-xs text-slate-500">Indique outras respostas sociais não listadas acima e o respetivo número de utentes.</p>
        <Textarea
          value={formData.outras_respostas_sociais}
          onChange={(e) => updateField('outras_respostas_sociais', e.target.value)}
          placeholder="Ex: Cantina Social - 150 utentes..."
          rows={3}
        />
      </div>

      {/* Total de Utentes - CALCULADO AUTOMATICAMENTE */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-semibold text-green-800">17. Total de Utentes Apoiados</Label>
            <p className="text-xs text-green-600 mt-1">Calculado automaticamente com base nas respostas sociais</p>
          </div>
          <div className="text-3xl font-bold text-green-700">
            {formData.total_utentes_apoiados || 0}
          </div>
        </div>
      </div>
    </div>
  );

  // SECÇÃO 4 - Informações Adicionais e Documentos
  const renderStep4 = () => (
    <div className="space-y-6" data-testid="step-4">
      {/* 18. Áreas ENTRAJUDA (NÃO obrigatório) */}
      <div className="space-y-3">
        <Label className="font-semibold">18. Assinale as áreas da ENTRAJUDA em que gostariam de receber apoio</Label>
        <p className="text-xs text-slate-500">(Opcional)</p>
        <div className="space-y-2">
          {AREAS_ENTRAJUDA.map(area => (
            <label key={area.id} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
              <Checkbox
                checked={formData.areas_interesse.includes(area.id)}
                onCheckedChange={() => toggleAreaInteresse(area.id)}
                className="mt-0.5"
              />
              <div>
                <span className="font-medium text-sm">{area.label}</span>
                <span className="text-slate-500 text-sm"> - {area.desc}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* 19. Comentários adicionais */}
      <div className="space-y-2">
        <Label className="font-semibold">19. Comentários adicionais</Label>
        <Textarea
          value={formData.comentarios_adicionais}
          onChange={(e) => updateField('comentarios_adicionais', e.target.value)}
          placeholder="Informações adicionais que considere relevantes..."
          rows={4}
        />
      </div>

      {/* 20. Candidatura submetida por */}
      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
        <Label className="font-semibold">20. Candidatura submetida por: *</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Nome *</Label>
            <Input
              id="field-submetido_por_nome"
              value={formData.submetido_por_nome}
              onChange={(e) => updateField('submetido_por_nome', e.target.value)}
              placeholder="Nome de quem submete"
              data-testid="input-submetido-nome"
              className={fieldErrors.submetido_por_nome ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors.submetido_por_nome && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.submetido_por_nome}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Cargo *</Label>
            <Select 
              value={formData.submetido_por_cargo} 
              onValueChange={(v) => updateField('submetido_por_cargo', v)}
            >
              <SelectTrigger id="field-submetido_por_cargo" data-testid="select-submetido-cargo" className={fieldErrors.submetido_por_cargo ? 'border-red-500' : ''}>
                <SelectValue placeholder="Selecione o cargo" />
              </SelectTrigger>
              <SelectContent>
                {CARGOS_CONTACTO.map(cargo => (
                  <SelectItem key={cargo} value={cargo}>{cargo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.submetido_por_cargo && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.submetido_por_cargo}</p>}
          </div>
        </div>
      </div>

      {/* 21. DOCUMENTOS OBRIGATÓRIOS */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#003366]" />
          <Label className="font-semibold text-lg">21. Documentos</Label>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-amber-800">
            <strong>Nota:</strong> A ENTRAJUDA pode contactar a instituição para solicitar documentação adicional em qualquer altura, caso seja necessário.
          </p>
        </div>

        {/* Documento Obrigatório: Estatutos */}
        <div className={`border rounded-lg p-4 ${fieldErrors.estatutos ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="font-medium">Estatutos da Instituição (assinados) *</Label>
              <p className="text-xs text-red-600">Obrigatório</p>
            </div>
            {formData.documentos.estatutos ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {formData.documentos.estatutos.nome}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeDocument('estatutos')}
                  className="text-red-500 hover:text-red-700"
                >
                  Remover
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleDocumentUpload('estatutos', e.target.files?.[0])}
                  className="hidden"
                />
                <Button variant="outline" size="sm" asChild disabled={uploadingDoc === 'estatutos'}>
                  <span>
                    {uploadingDoc === 'estatutos' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A carregar...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Carregar</>
                    )}
                  </span>
                </Button>
              </label>
            )}
          </div>
          {fieldErrors.estatutos && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.estatutos}</p>}
        </div>

        {/* Documentos Opcionais */}
        <p className="text-sm text-slate-600 mt-4 font-medium">Se aplicável:</p>
        
        <div className="border rounded-lg p-4 border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="font-medium">Relatório e Contas (último ano - aprovado e assinado)</Label>
              <p className="text-xs text-slate-500">Opcional</p>
            </div>
            {formData.documentos.relatorio_contas ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {formData.documentos.relatorio_contas.nome}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeDocument('relatorio_contas')}
                  className="text-red-500 hover:text-red-700"
                >
                  Remover
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleDocumentUpload('relatorio_contas', e.target.files?.[0])}
                  className="hidden"
                />
                <Button variant="outline" size="sm" asChild disabled={uploadingDoc === 'relatorio_contas'}>
                  <span>
                    {uploadingDoc === 'relatorio_contas' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A carregar...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Carregar</>
                    )}
                  </span>
                </Button>
              </label>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4 border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="font-medium">Plano de Atividades</Label>
              <p className="text-xs text-slate-500">Opcional</p>
            </div>
            {formData.documentos.plano_atividades ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {formData.documentos.plano_atividades.nome}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeDocument('plano_atividades')}
                  className="text-red-500 hover:text-red-700"
                >
                  Remover
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleDocumentUpload('plano_atividades', e.target.files?.[0])}
                  className="hidden"
                />
                <Button variant="outline" size="sm" asChild disabled={uploadingDoc === 'plano_atividades'}>
                  <span>
                    {uploadingDoc === 'plano_atividades' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A carregar...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Carregar</>
                    )}
                  </span>
                </Button>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Termos */}
      <div className="p-4 bg-slate-50 rounded-lg space-y-4 mt-6 border">
        <p className="text-sm text-slate-600">
          Clique em <strong>Concluído</strong> para enviar a candidatura ou em <strong>Voltar</strong> se pretender rever.
        </p>
        <div className="flex items-start gap-2">
          <Checkbox
            id="aceita_termos"
            checked={formData.aceita_termos}
            onCheckedChange={(v) => updateField('aceita_termos', v)}
            data-testid="checkbox-termos"
          />
          <Label htmlFor="aceita_termos" className="cursor-pointer text-sm">
            Li e aceito os <a href="/termos" className="text-[#0066CC] underline" target="_blank">Termos de Uso</a> e 
            a <a href="/privacidade" className="text-[#0066CC] underline" target="_blank">Política de Privacidade</a> *
          </Label>
        </div>
        {fieldErrors.aceita_termos && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors.aceita_termos}</p>}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  const stepTitles = [
    { icon: Building2, title: '1 - Identificação da Instituição', desc: 'Dados básicos, morada, presidente e contactos' },
    { icon: FileText, title: '2 - Caracterização da Instituição', desc: 'Forma jurídica, missão, área geográfica e equipa' },
    { icon: Users, title: '3 - Respostas Sociais', desc: 'Serviços, acordos e utentes apoiados' },
    { icon: HandHeart, title: '4 - Documentos e Conclusão', desc: 'Áreas de apoio, documentos e submissão' }
  ];

  return (
    <div className="min-h-screen bg-slate-100 py-8" data-testid="portal-registar-page">
      <div className="container max-w-3xl mx-auto px-4">
        {/* Header with Logo */}
        <div className="text-center mb-6">
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl font-bold text-[#003366]">ENTR</span>
              <span className="text-3xl font-bold text-[#CC0033]">A</span>
              <span className="text-3xl font-bold text-[#003366]">JUDA</span>
            </div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Apoio a Instituições de Solidariedade Social</p>
          </div>
        </div>

        {/* Main Title Bar */}
        <div className="bg-[#003366] text-white py-3 px-6 rounded-t-lg">
          <h1 className="text-lg font-bold uppercase tracking-wide">Candidatura a Apoio</h1>
        </div>

        {/* Section Header */}
        <div className="bg-slate-200 py-2 px-6 border-b">
          <h2 className="text-sm font-semibold text-slate-700">{stepTitles[step - 1].title}</h2>
        </div>

        {/* Progress */}
        <div className="bg-white px-6 py-3 border-b">
          <Progress value={(step / 4) * 100} className="h-2" />
          <p className="text-xs text-slate-500 mt-1 text-right">Passo {step} de 4</p>
        </div>

        {/* Form Card */}
        <Card className="rounded-t-none shadow-lg border-t-0">
          <CardContent className="pt-6">
            {renderStep()}

            {/* Navigation */}
            <div className="flex justify-center gap-4 mt-8 pt-6 border-t">
              {step > 1 && (
                <Button 
                  variant="default" 
                  onClick={prevStep}
                  className="bg-[#003366] hover:bg-[#002244] min-w-[120px]"
                >
                  Voltar
                </Button>
              )}

              {step < 4 ? (
                <Button 
                  onClick={nextStep} 
                  className="bg-[#003366] hover:bg-[#002244] min-w-[120px]"
                >
                  Continuar
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={loading || !formData.aceita_termos}
                  className="bg-[#003366] hover:bg-[#002244] min-w-[120px]"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A submeter...
                    </>
                  ) : (
                    'Concluído'
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
